import axios, { AxiosError } from 'axios';
import {
  MonthlyStatistics,
  Statistics,
  FunnelPerStageStatistics,
  User,
  DroppedReason,
  BenchmarkStatistics,
} from '@tassomutuo-backoffice/types';

import { appendHsOwnerIdIfPresentAndAdmin } from '../../../utils/apiUtilis';
import { axiosApiInstancePrivate } from '..';
import { endOfDay, endOfMonth, startOfMonth, subMonths } from 'date-fns';

export interface MemberStatistic {
  statistics: null | {
    statisticsAllPeriod: Statistics;
    statisticsAllPeriodTrend: Statistics;
    statisticsPerMonth: MonthlyStatistics;
    funnelPerStageStatistics: FunnelPerStageStatistics;
    droppedReasons: DroppedReason;
  };
  user: Pick<User, 'id' | 'hubspotOwnerId' | 'name' | 'email'>;
}

interface StatisticsResponse {
  statisticsAllPeriod: Statistics;
  statisticsAllPeriodTrend: Statistics;
  statisticsPerMonth: MonthlyStatistics;
  funnelPerStageStatistics: FunnelPerStageStatistics;
  droppedReasons: DroppedReason;
  isTeamStatistics: boolean;
  team: string;
  memberStatistics: MemberStatistic[];
  lostRevenueLeads?: Record<string, string>[]; // Aggiunto per tenere traccia dei lead con fatturato perso
}

export type Period =
  | 'last_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'custom';

// Helper function per calcolare le date dai periodi predefiniti
const calculateDatesFromPeriod = (
  period: Period
): { startDate: Date; endDate: Date } => {
  const endDate = endOfDay(endOfMonth(subMonths(new Date(), 1)));
  let startDate: Date;

  switch (period) {
    case 'last_month':
      startDate = startOfMonth(endDate);
      break;
    case 'last_3_months':
      startDate = startOfMonth(subMonths(endDate, 2));
      break;
    case 'last_6_months':
      startDate = startOfMonth(subMonths(endDate, 5));
      break;
    case 'last_12_months':
      startDate = startOfMonth(subMonths(endDate, 11));
      break;
    default:
      throw new Error(`Unknown period: ${period}`);
  }

  return { startDate, endDate };
};

export type ComparisonPeriod = 'previous_period' | 'previous_year';

// Overloaded function signatures per supportare sia Period che Date range
export async function getStatistics(
  period: Period,
  teamMemberHsOwnerId?: string,
  groupId?: string | null,
  startDate?: undefined,
  endDate?: undefined,
  comparisonPeriod?: ComparisonPeriod
): Promise<StatisticsResponse>;

export async function getStatistics(
  period: undefined,
  teamMemberHsOwnerId: string | undefined,
  groupId: string | null | undefined,
  startDate: Date,
  endDate: Date,
  comparisonPeriod?: ComparisonPeriod
): Promise<StatisticsResponse>;

export async function getStatistics(
  period?: Period,
  teamMemberHsOwnerId?: string,
  groupId?: string | null,
  customStartDate?: Date,
  customEndDate?: Date,
  comparisonPeriod?: ComparisonPeriod
): Promise<StatisticsResponse> {
  let startDate: Date;
  let endDate: Date;

  // Se sono fornite date personalizzate, usale
  if (customStartDate && customEndDate) {
    startDate = new Date(customStartDate);
    endDate = new Date(customEndDate);
  }
  // Altrimenti calcola dalle date dal periodo
  else if (period) {
    ({ startDate, endDate } = calculateDatesFromPeriod(period));
  }
  // Errore se non è fornito né period né date personalizzate
  else {
    throw new Error('Either period or custom date range must be provided');
  }

  try {
    let url = `/v1/statistics?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&teamMemberHsOwnerId=${
      teamMemberHsOwnerId || ''
    }&groupId=${groupId || ''}`;

    if (comparisonPeriod) {
      url += `&comparisonPeriod=${comparisonPeriod}`;
    }

    const response = await axiosApiInstancePrivate.get<StatisticsResponse>(
      appendHsOwnerIdIfPresentAndAdmin(url)
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

// Overloaded function signatures per getBenchmarkStatistics
export async function getBenchmarkStatistics(
  period: Period
): Promise<BenchmarkStatistics>;

export async function getBenchmarkStatistics(
  period: undefined,
  startDate: Date,
  endDate: Date
): Promise<BenchmarkStatistics>;

export async function getBenchmarkStatistics(
  period?: Period,
  customStartDate?: Date,
  customEndDate?: Date
): Promise<BenchmarkStatistics> {
  let startDate: Date;
  let endDate: Date;

  // Se sono fornite date personalizzate, usale
  if (customStartDate && customEndDate) {
    startDate = new Date(customStartDate);
    endDate = new Date(customEndDate);
  }
  // Altrimenti calcola le date dal periodo
  else if (period) {
    ({ startDate, endDate } = calculateDatesFromPeriod(period));
  }
  // Errore se non è fornito né period né date personalizzate
  else {
    throw new Error('Either period or custom date range must be provided');
  }

  try {
    const response = await axiosApiInstancePrivate.get<BenchmarkStatistics>(
      `/v1/statistics/benchmark?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

// Overloaded function signatures per getBenchmarkStatistics
export async function getAdminBenchmarkStatistics(
  period: Period,
  startDate?: undefined,
  endDate?: undefined,
  region?: string | null,
  province?: string | null,
  comparisonPeriod?: ComparisonPeriod
): Promise<BenchmarkStatistics>;

export async function getAdminBenchmarkStatistics(
  period: undefined,
  startDate: Date,
  endDate: Date,
  region?: string | null,
  province?: string | null,
  comparisonPeriod?: ComparisonPeriod
): Promise<BenchmarkStatistics>;

export async function getAdminBenchmarkStatistics(
  period?: Period,
  customStartDate?: Date,
  customEndDate?: Date,
  region?: string | null,
  province?: string | null,
  comparisonPeriod?: ComparisonPeriod
): Promise<BenchmarkStatistics> {
  let startDate: Date;
  let endDate: Date;

  // Se sono fornite date personalizzate, usale
  if (customStartDate && customEndDate) {
    startDate = new Date(customStartDate);
    endDate = new Date(customEndDate);
  }
  // Altrimenti calcola le date dal periodo
  else if (period) {
    ({ startDate, endDate } = calculateDatesFromPeriod(period));
  }
  // Errore se non è fornito né period né date personalizzate
  else {
    throw new Error('Either period or custom date range must be provided');
  }

  try {
    let url = `/v1/statistics/admin/benchmark?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;

    if (region) {
      url += `&region=${encodeURIComponent(region)}`;
    }

    if (province) {
      url += `&province=${encodeURIComponent(province)}`;
    }

    if (comparisonPeriod) {
      url += `&comparisonPeriod=${comparisonPeriod}`;
    }

    const response = await axiosApiInstancePrivate.get<BenchmarkStatistics>(
      url
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

export async function getRegionsListForStatistics(): Promise<string[]> {
  try {
    const response = await axiosApiInstancePrivate.get<{ regions: string[] }>(
      `/v1/statistics/regions`
    );

    return response.data.regions;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

export async function getProvincesListForStatistics(
  region: string | null
): Promise<string[]> {
  try {
    const response = await axiosApiInstancePrivate.get<{ provinces: string[] }>(
      `/v1/statistics/provinces?region=${region || ''}`
    );

    return response.data.provinces;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}
